/**
 * TelcaVoIP Cloud — extras (Fabrizio CR 28/02/2026 — backlog completion):
 *   1. Token-age warning banner (≤7 days before pCloud 31-day TTL elapses)
 *   2. 12-month mini history bar chart (success vs error per month)
 *   3. Recent backups list with one-click download/restore
 *   4. Rotate-token modal (paste fresh token from pCloud DevTools)
 *
 * State is local — this component owns its own API calls so the parent
 * (AdminBackupPanel + AdminDashboard) doesn't need to thread additional
 * props. It piggy-backs on the same Bearer token that `axios` already
 * carries from the global interceptor in `AdminDashboard`.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, RefreshCw, KeyRound, Download, History, X, ShieldCheck, ExternalLink } from "lucide-react";
import { API } from "../../config";
import { toast } from "sonner";

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export const TelcavoipCloudExtras = ({ status }) => {
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [rotating, setRotating] = useState(false);

  // ── OAuth 2.0 permanent-token setup (Fabrizio CR 22/02/2026) ──
  // Once the admin exchanges the one-time authorization code with
  // pCloud, the durable OAuth token becomes the primary auth source
  // and the session-token in .env fades to a fallback role.
  const [oauthState, setOauthState] = useState({ loading: true, active: false, authorize_url: null });
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const [oauthCode, setOauthCode] = useState("");
  const [oauthBusy, setOauthBusy] = useState(false);

  const loadOAuthState = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/backup/telcavoip-cloud/oauth-authorize-url`);
      setOauthState({
        loading: false,
        active: !!res.data?.already_active,
        authorize_url: res.data?.authorize_url || null,
        ok: !!res.data?.ok,
        error: res.data?.error,
      });
    } catch {
      setOauthState({ loading: false, active: false, authorize_url: null });
    }
  }, []);

  useEffect(() => { loadOAuthState(); }, [loadOAuthState]);

  const activateOAuth = async () => {
    const code = oauthCode.trim();
    if (code.length < 6) {
      toast.error("Codice troppo corto. Copialo dalla pagina pCloud.");
      return;
    }
    setOauthBusy(true);
    try {
      const res = await axios.post(`${API}/backup/telcavoip-cloud/oauth-setup`, { code });
      if (res.data?.ok) {
        toast.success(`OAuth attivato per ${res.data.validated_for_email || "l'account pCloud"}`);
        setShowOAuthModal(false);
        setOauthCode("");
        loadOAuthState();
      } else {
        toast.error(`Attivazione fallita: ${res.data?.message || res.data?.error}`);
      }
    } catch (e) {
      toast.error("Attivazione fallita: " + (e.response?.data?.message || e.message));
    } finally { setOauthBusy(false); }
  };

  const revokeOAuth = async () => {
    if (!window.confirm("Revocare l'OAuth token permanente? Il backup tornerà a usare il session-token .env come fallback.")) return;
    setOauthBusy(true);
    try {
      const res = await axios.post(`${API}/backup/telcavoip-cloud/oauth-revoke`);
      if (res.data?.ok) {
        toast.success("OAuth revocato. Fallback su session-token .env attivo.");
        loadOAuthState();
      }
    } catch (e) {
      toast.error("Revoca fallita: " + (e.response?.data?.message || e.message));
    } finally { setOauthBusy(false); }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/backup/telcavoip-cloud/history?months=12`);
      setHistory(res.data?.history || []);
    } catch (e) { setHistory([]); }
    finally { setHistoryLoading(false); }
  }, []);

  const loadBackups = useCallback(async () => {
    setBackupsLoading(true);
    try {
      const res = await axios.get(`${API}/backup/telcavoip-cloud/list`);
      setBackups(res.data?.items || []);
    } catch (e) { setBackups([]); }
    finally { setBackupsLoading(false); }
  }, []);

  useEffect(() => {
    if (status?.enabled && status?.configured) {
      loadHistory();
      loadBackups();
    }
  }, [status?.enabled, status?.configured, status?.last_upload_at, loadHistory, loadBackups]);

  const downloadBackup = async (item) => {
    setDownloadingId(item.fileid);
    try {
      const res = await axios.post(
        `${API}/backup/telcavoip-cloud/restore?fileid=${item.fileid}&filename=${encodeURIComponent(item.name)}`,
        null,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = item.name; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup ${item.name} scaricato`);
    } catch (e) {
      toast.error("Download fallito: " + (e.response?.data?.message || e.message));
    } finally { setDownloadingId(null); }
  };

  const submitRotate = async () => {
    if (!newToken || newToken.trim().length < 20) {
      toast.error("Token troppo corto (minimo 20 caratteri)");
      return;
    }
    setRotating(true);
    try {
      const res = await axios.post(`${API}/backup/telcavoip-cloud/rotate-token`, { token: newToken.trim() });
      if (res.data?.ok) {
        toast.success(`Token rotato OK (validato per ${res.data.validated_for_email})`);
        setShowRotateModal(false);
        setNewToken("");
        // Refresh status so the banner disappears
        window.location.reload();
      } else {
        toast.error(`Rotazione fallita: ${res.data?.message || res.data?.error}`);
      }
    } catch (e) {
      toast.error("Rotazione fallita: " + (e.response?.data?.message || e.message));
    } finally { setRotating(false); }
  };

  if (!status?.configured) return null;

  // Max success_count across history rows → for bar scaling
  const maxCount = Math.max(1, ...history.map((m) => Math.max(m.success_count, m.error_count)));

  return (
    <div className="mt-4 space-y-4" data-testid="telcavoip-cloud-extras">
      {/* ── OAuth 2.0 permanent token setup ──
          Displayed only when Client ID/Secret are configured in .env.
          Two states:
            - NOT active → invite to activate with a one-time browser flow
            - ACTIVE → confirm current status + optional revoke */}
      {oauthState.ok && !oauthState.active && (
        <div
          className="bg-emerald-950/30 border border-emerald-500/40 rounded-lg p-3 flex items-start gap-3"
          data-testid="telcavoip-oauth-setup-invite"
        >
          <ShieldCheck size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-emerald-200 text-sm font-medium">
              Attiva l&apos;OAuth token permanente pCloud
            </p>
            <p className="text-emerald-200/70 text-xs mt-1 leading-snug">
              Il token OAuth permanente sostituisce definitivamente il session-token
              del browser: nessuna scadenza, resiste ai restart e ai portali down.
              Attivazione in 2 click (~30 secondi).
            </p>
          </div>
          <button
            onClick={() => setShowOAuthModal(true)}
            className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 rounded-md text-xs font-medium flex items-center gap-1"
            data-testid="telcavoip-oauth-activate-btn"
          >
            <ShieldCheck size={12} /> Attiva OAuth
          </button>
        </div>
      )}
      {oauthState.active && (
        <div
          className="bg-emerald-950/20 border border-emerald-500/25 rounded-lg p-3 flex items-center gap-3"
          data-testid="telcavoip-oauth-active"
        >
          <ShieldCheck size={16} className="text-emerald-400 flex-shrink-0" />
          <p className="text-emerald-200 text-xs flex-1">
            <span className="font-semibold">OAuth permanente attivo</span> — il backup usa questo
            token come sorgente primaria. Il session-token .env resta come fallback di sicurezza.
          </p>
          <button
            onClick={revokeOAuth}
            disabled={oauthBusy}
            className="text-emerald-300/60 hover:text-red-300 text-[11px] underline"
            data-testid="telcavoip-oauth-revoke-btn"
          >
            Revoca
          </button>
        </div>
      )}
      {/* Token expiry banner */}
      {status?.token_expiring_soon && (
        <div className="bg-amber-950/40 border border-amber-500/40 rounded-lg p-3 flex items-start gap-3" data-testid="telcavoip-token-expiring">
          <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-amber-200 text-sm font-medium">
              Token pCloud in scadenza tra {status.token_expires_in_days} giorni
            </p>
            <p className="text-amber-200/70 text-xs mt-1">
              Registrato il {fmtDate(status.token_registered_at)}. Estrai un nuovo token
              dalla sessione browser pCloud (F12 → Network → `auth=`) e ruotalo qui sotto.
            </p>
          </div>
          <button
            onClick={() => setShowRotateModal(true)}
            className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded-md text-xs font-medium flex items-center gap-1"
            data-testid="telcavoip-rotate-cta"
          >
            <KeyRound size={12} /> Ruota token
          </button>
        </div>
      )}

      {/* Token info (always visible when configured) */}
      <div className="flex items-center justify-between text-xs bg-[#0A0A0A] rounded-lg p-3">
        <span className="text-gray-400">Token pCloud</span>
        <div className="flex items-center gap-3">
          <span className="text-white">
            età {status.token_age_days ?? "—"}g · scade tra {status.token_expires_in_days ?? "—"}g
          </span>
          <button
            onClick={() => setShowRotateModal(true)}
            className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
            data-testid="telcavoip-rotate-btn"
          >
            <KeyRound size={11} /> Ruota
          </button>
        </div>
      </div>

      {/* Consecutive errors banner */}
      {status?.consecutive_errors >= 1 && (
        <div className="bg-red-950/40 border border-red-500/40 rounded-lg p-3" data-testid="telcavoip-consec-errors">
          <p className="text-red-300 text-xs">
            ⚠ {status.consecutive_errors} fallimento{status.consecutive_errors > 1 ? "i" : ""} consecutivi.
            {status.consecutive_errors >= 2 && " Alert email inviata a info.securewallet@lojack.eu."}
          </p>
        </div>
      )}

      {/* 12-month mini chart */}
      <div className="bg-[#0A0A0A] rounded-lg p-3" data-testid="telcavoip-history-chart">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 flex items-center gap-1"><History size={12} /> Backup ultimi 12 mesi</span>
          <button onClick={loadHistory} disabled={historyLoading}
            className="text-gray-500 hover:text-white" data-testid="telcavoip-history-refresh">
            <RefreshCw size={12} className={historyLoading ? "animate-spin" : ""} />
          </button>
        </div>
        {history.length > 0 ? (
          <div className="flex items-end justify-between gap-1 h-24">
            {history.map((m) => {
              const pct = (m.success_count / maxCount) * 100;
              const hasErr = m.error_count > 0;
              const totalRuns = m.success_count + m.error_count;
              const color = hasErr && m.success_count === 0 ? "bg-red-500"
                          : hasErr ? "bg-amber-500"
                          : m.success_count > 0 ? "bg-emerald-500"
                          : "bg-gray-800";
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center" title={`${m.month}: ${m.success_count} OK${hasErr ? `, ${m.error_count} errori` : ""} · ${m.total_size_kb} KB`}>
                  <div className="w-full flex-1 flex items-end" style={{ minHeight: "60px" }}>
                    <div
                      className={`w-full ${color} rounded-t transition-all`}
                      style={{ height: `${Math.max(pct, totalRuns > 0 ? 8 : 0)}%` }}
                    />
                  </div>
                  <span className="text-[9px] text-gray-500 mt-1 truncate w-full text-center">
                    {m.month.slice(5)}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-600 text-center py-4">Nessun dato disponibile</p>
        )}
        <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> OK</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-sm" /> misti</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm" /> tutti KO</span>
        </div>
      </div>

      {/* Recent backups list with download */}
      <div className="bg-[#0A0A0A] rounded-lg p-3" data-testid="telcavoip-backups-list">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Download size={12} /> Backup mese corrente ({backups.length})
          </span>
          <button onClick={loadBackups} disabled={backupsLoading}
            className="text-gray-500 hover:text-white" data-testid="telcavoip-list-refresh">
            <RefreshCw size={12} className={backupsLoading ? "animate-spin" : ""} />
          </button>
        </div>
        {backups.length > 0 ? (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {backups.map((b) => (
              <div key={b.fileid} className="flex items-center justify-between bg-[#111] rounded px-3 py-2 text-xs hover:bg-[#1a1a1a]" data-testid={`telcavoip-backup-row-${b.fileid}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-white truncate" title={b.name}>{b.name}</p>
                  <p className="text-gray-500 text-[10px]">{fmtDate(b.created)} · {fmtBytes(b.size_bytes)}</p>
                </div>
                <button
                  onClick={() => downloadBackup(b)}
                  disabled={downloadingId === b.fileid}
                  className="ml-2 px-2.5 py-1 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded text-[10px] font-medium flex items-center gap-1"
                  data-testid={`telcavoip-download-${b.fileid}`}
                >
                  {downloadingId === b.fileid ? <RefreshCw size={10} className="animate-spin" /> : <Download size={10} />}
                  Scarica
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-600 text-center py-4">Nessun backup nel mese corrente</p>
        )}
        <p className="text-[10px] text-gray-600 mt-2">
          ℹ Il download produce il file JSON. Il restore manuale al DB avviene via `mongoimport` o lo script `scripts/restore_backup.py`.
        </p>
      </div>

      {/* OAuth-setup modal (Fabrizio CR 22/02/2026) */}
      {showOAuthModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setShowOAuthModal(false)}
          data-testid="telcavoip-oauth-modal"
        >
          <div
            className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-5 max-w-xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-400" /> Attiva OAuth permanente pCloud
              </h3>
              <button
                onClick={() => setShowOAuthModal(false)}
                className="text-gray-500 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-gray-400 text-xs mb-3 leading-snug">
              Esegui il flow OAuth 2.0 <strong>una sola volta</strong> per ottenere un
              token permanente che sostituisce il session-token temporaneo del browser.
            </p>

            <ol className="text-xs text-gray-400 list-decimal pl-5 mb-4 space-y-2">
              <li>
                Clicca il bottone sotto per aprire la pagina di consenso pCloud in una nuova
                scheda del browser <strong>già loggato come <code className="text-gray-200">info@telcavoip.eu</code></strong>.
                <a
                  href={oauthState.authorize_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 rounded text-[11px]"
                  data-testid="telcavoip-oauth-open-authorize"
                >
                  <ExternalLink size={11} /> Apri pagina di autorizzazione pCloud
                </a>
              </li>
              <li>Nella pagina pCloud clicca <strong>&quot;Allow&quot;</strong> per autorizzare l&apos;app.</li>
              <li>
                pCloud mostrerà un <strong>codice a 8-10 caratteri</strong> (o lo aggiungerà
                all&apos;URL come <code className="text-gray-200">?code=xxx</code>). Copia il codice
                e incollalo qui sotto.
              </li>
            </ol>

            <label className="text-gray-400 text-[11px] block mb-1">
              Codice di autorizzazione ricevuto:
            </label>
            <input
              type="text"
              placeholder="es. QzAbCdEfGhIj"
              value={oauthCode}
              onChange={(e) => setOauthCode(e.target.value)}
              className="w-full px-3 py-2 bg-[#111] border border-gray-700 rounded text-white text-sm font-mono mb-3"
              autoComplete="off"
              data-testid="telcavoip-oauth-code-input"
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowOAuthModal(false)}
                className="px-3 py-1.5 text-gray-400 hover:text-white text-sm"
                data-testid="telcavoip-oauth-cancel"
              >
                Annulla
              </button>
              <button
                onClick={activateOAuth}
                disabled={oauthBusy || !oauthCode.trim()}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm flex items-center gap-1"
                data-testid="telcavoip-oauth-submit"
              >
                {oauthBusy && <RefreshCw size={12} className="animate-spin" />} Attiva token permanente
              </button>
            </div>

            <p className="text-[10px] text-gray-600 mt-3">
              ⚠ Il token viene salvato in Mongo (<code>backup_settings.pcloud_oauth_access_token</code>)
              e verificato con <code>/userinfo</code>. Nessun redeploy richiesto: la modifica
              è attiva immediatamente per tutti i backup schedulati.
            </p>
          </div>
        </div>
      )}

      {/* Rotate-token modal */}
      {showRotateModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowRotateModal(false)} data-testid="telcavoip-rotate-modal">
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-5 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2"><KeyRound size={16} className="text-cyan-400" /> Ruota token pCloud</h3>
              <button onClick={() => setShowRotateModal(false)} className="text-gray-500 hover:text-white"><X size={16} /></button>
            </div>
            <p className="text-gray-400 text-xs mb-3">
              Estrai un nuovo token dalla sessione browser pCloud:
            </p>
            <ol className="text-xs text-gray-400 list-decimal pl-4 mb-3 space-y-1">
              <li>Login su https://my.pcloud.com</li>
              <li>F12 → Network → click qualsiasi cartella</li>
              <li>Cerca `auth=` nelle query string</li>
              <li>Copia il valore (~40 caratteri) e incollalo qui sotto</li>
            </ol>
            <input
              type="text"
              placeholder="es. dPIKHkZjidXZPPkTqVFuc3fwNQ3py4C2XR7c88s7"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              className="w-full px-3 py-2 bg-[#111] border border-gray-700 rounded text-white text-sm font-mono mb-3"
              data-testid="telcavoip-rotate-input"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowRotateModal(false)} className="px-3 py-1.5 text-gray-400 hover:text-white text-sm" data-testid="telcavoip-rotate-cancel">
                Annulla
              </button>
              <button onClick={submitRotate} disabled={rotating || !newToken.trim()}
                className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded text-sm flex items-center gap-1"
                data-testid="telcavoip-rotate-submit">
                {rotating && <RefreshCw size={12} className="animate-spin" />} Valida & ruota
              </button>
            </div>
            <p className="text-[10px] text-gray-600 mt-3">
              ⚠ Il token viene validato con `/userinfo` prima di essere adottato. Per persistenza oltre il restart, aggiorna anche `PCLOUD_ACCESS_TOKEN` nelle env-vars di produzione.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelcavoipCloudExtras;
